import { cn } from '@/lib/cn'
import useCustomTabBarHeight from '@/lib/hooks/useCustomTabBarHeight'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import { lighten } from '@/lib/utils/colors'
import type { TableRow, TableSection, TableViewProps } from '@modules/table-view'
import { useTheme } from "expo-router/react-navigation"
import { FlashList } from '@shopify/flash-list'
import { forEach, isEmpty, trim } from 'lodash-es'
import { CheckIcon, ChevronRightIcon, ChevronsUpDownIcon, LucideIcon } from 'lucide-react-native'
import * as React from 'react'
import { Platform, StyleSheet, Switch, View } from 'react-native'
import SFSymbolesIconsMap from './SFSymbolesIconsMap'
import { Divider } from './ui/divider'
import { HStack } from './ui/hstack'
import { Image } from './ui/image'
import { Menu, MenuItem } from './ui/menu'
import { Pressable } from './ui/pressable'
import { Text } from './ui/text'
import { VStack } from './ui/vstack'

export type { TableRow, TableSection, TableViewProps } from '@modules/table-view'

export default function TableView(props: TableViewProps) {
  const theme = useTheme()
  const customTabBarHeight = useCustomTabBarHeight()
  const [secondaryLabelColor, systemBackgroundColor] = usePlatformColor(
    'secondaryLabel',
    'systemGroupedBackgroundColor'
  )

  const switchTrackColor = React.useMemo(() => {
    return { true: lighten(theme.colors.primary as string, 0.5), false: theme.colors.border }
  }, [theme.colors.primary, theme.colors.border])

  const data = React.useMemo(() => {
    let sections: TableSection[] | undefined
    if (props.sections) {
      sections = props.sections
    } else if (props.rows) {
      sections = [{ title: '', rows: props.rows }]
    }

    if (!sections?.length) return []

    const dataItems: (
      | { type: 'sectionHeader'; title: string; sectionIndex: number }
      | {
          type: 'row'
          section: TableSection
          row: TableRow & { IconComponent?: LucideIcon; iconUrl?: string }
          rowIndex: number
        }
    )[] = []
    forEach(sections, (section, sectionIndex) => {
      dataItems.push({ type: 'sectionHeader', title: section.title, sectionIndex })
      forEach(section.rows, (row, rowIndex) => {
        dataItems.push({
          type: 'row',
          section,
          row: {
            ...row,
            IconComponent: row.icon ? SFSymbolesIconsMap[row.icon] : undefined,
            iconUrl: row.icon?.match(/^https?:\/\//) ? row.icon : undefined,
          },
          rowIndex,
        })
      })
    })
    return dataItems
  }, [props.sections, props.rows])

  const renderHeader = React.useCallback(() => {
    if (!props.headerText) return null
    return (
      <Text size="md" className="px-5 pt-2 pb-4" style={{ color: secondaryLabelColor }}>
        {props.headerText}
      </Text>
    )
  }, [props.headerText, secondaryLabelColor])

  const renderFooter = React.useCallback(() => {
    if (!props.footerText) return null
    return (
      <Text size="sm" className="px-5 pt-2 pb-4" style={{ color: secondaryLabelColor }}>
        {props.footerText}
      </Text>
    )
  }, [props.footerText, secondaryLabelColor])

  return (
    <FlashList
      style={StyleSheet.flatten([
        { backgroundColor: systemBackgroundColor },
        props.isScrollEnabled !== false ? { flex: 1 } : {},
        props.style,
      ])}
      scrollEnabled={Platform.OS === 'web' || props.isScrollEnabled !== false}
      contentContainerStyle={{ paddingTop: 20, paddingBottom: 20 + customTabBarHeight, ...props.contentContainerStyle }}
      refreshing={props.onRefresh ? props.refreshing : undefined}
      onRefresh={props.onRefresh}
      data={data}
      keyExtractor={(_item, index) => index.toString()}
      ListHeaderComponent={renderHeader}
      ListFooterComponent={renderFooter}
      renderItem={({ item: dataItem }) => {
        if (dataItem.type === 'sectionHeader') {
          if (isEmpty(trim(dataItem.title ?? ''))) {
            if (dataItem.sectionIndex === 0) return null
            return <View className="h-9" />
          }
          return (
            <Text size="lg" bold className="px-9 text-typography-600 mt-4 mb-1">
              {dataItem.title}
            </Text>
          )
        }
        const { row, rowIndex, section } = dataItem
        const isLastRowInSection = rowIndex === section.rows.length - 1
        const isSwitchType = row.type === 'switch'
        const isRadioType = row.type === 'radio'
        const isMenuType = row.type === 'menu'
        const isActionType = row.type === 'action'
        return (
          <>
            <Pressable
              className={`mx-5 bg-secondary-system-grouped-background ${!isSwitchType ? 'data-[active=true]:bg-background-50' : ''} ${rowIndex === 0 ? 'rounded-t-3xl' : ''} ${isLastRowInSection ? 'rounded-b-3xl' : ''}`}
              onPress={isSwitchType ? undefined : row.onPress}
            >
              <HStack className="p-4 items-center overflow-hidden" space="md">
                {row.IconComponent ? (
                  <row.IconComponent size={row.iconSize === 'lg' ? 40 : 24} color={theme.colors.primary} />
                ) : row.iconUrl ? (
                  <Image
                    source={{ uri: row.iconUrl }}
                    size="none"
                    className={`rounded-full ${row.iconSize === 'lg' ? 'w-10 h-10' : 'w-6 h-6'}`}
                    alt={row.title}
                  />
                ) : null}
                {row.detailText ? (
                  // Value cell style: title on left, detail text on right
                  (<>
                    <Text size="lg" className="flex-shrink-0 max-w-[50%] break-all">
                      {row.title}
                    </Text>
                    <Text size="lg" className="text-typography-500 flex-1 min-w-0 text-right break-all">
                      {row.detailText}
                    </Text>
                  </>)
                ) : (
                  // Regular cell style: title with optional subtitle below
                  (<VStack space="xs" className={cn('flex-1 min-w-0 overflow-hidden', isActionType && 'items-center')}>
                    <Text
                      size="lg"
                      className={cn('block w-full', row.actionType === 'destructive' && 'text-error-400')}
                    >
                      {row.title}
                    </Text>
                    {row.subTitle && (
                      <Text size="md" className="text-typography-500 break-all">
                        {row.subTitle}
                      </Text>
                    )}
                  </VStack>)
                )}
                {isSwitchType ? (
                  <Switch
                    value={row.switchValue}
                    onValueChange={row.onValueChange}
                    disabled={!row.onValueChange}
                    // @ts-ignore
                    activeThumbColor={theme.colors.primary}
                    trackColor={switchTrackColor}
                  />
                ) : isRadioType && row.radioSelected ? (
                  <CheckIcon size={24} color={theme.colors.primary} />
                ) : isMenuType && row.menuSelectedTitle ? (
                  // Menu type: show selected text + chevron up/down icon
                  (<MenuRowAction row={row} />)
                ) : (
                  row.onPress &&
                  !isRadioType &&
                  !isActionType && <ChevronRightIcon size={24} color={theme.colors.border} />
                )}
              </HStack>
            </Pressable>
            {rowIndex < section.rows.length - 1 && (
              <View className="mx-5">
                <Divider className="bg-separator-vibrant" />
              </View>
            )}
          </>
        );
      }}
    />
  );
}

function MenuRowAction({ row }: { row: Pick<TableRow, 'menuItems' | 'onMenuItemSelect' | 'menuSelectedTitle'> }) {
  const theme = useTheme()

  return (
    <Menu
      trigger={props => (
        <Pressable {...props}>
          <HStack space="xs" className="items-center">
            <Text size="md" className="text-typography-500">
              {row.menuSelectedTitle}
            </Text>
            <ChevronsUpDownIcon size={13} color={theme.colors.border} />
          </HStack>
        </Pressable>
      )}
    >
      {row.menuItems?.map(menuItem => (
        <MenuItem key={menuItem.id} textValue={menuItem.title} onPress={() => row.onMenuItemSelect?.(menuItem.id)}>
          <Text size="md" className="text-typography-500">
            {menuItem.title}
          </Text>
        </MenuItem>
      ))}
    </Menu>
  )
}
